import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment-jalaali';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useUserInfoStore } from '@entities/user';
import { IErrorResponse } from '@shared/api';

import { submitInsuranceProfile } from '../api';
import { profileSchema } from './validations';

type ProfileFormData = z.infer<typeof profileSchema>;

type UseUpdateProfileParams = {
  open: boolean;
  onSuccess: () => void;
};

const SLOW_RESPONSE_THRESHOLD_MS = 5000;

export const useUpdateProfile = ({ open, onSuccess }: UseUpdateProfileParams) => {
  const customerInfo = useUserInfoStore((state) => state.data);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSlowResponseWarning, setShowSlowResponseWarning] = useState(false);

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    mode: 'onSubmit',
    defaultValues: {
      birthDate: customerInfo?.birthDate
        ? moment(customerInfo.birthDate).format('jYYYY/jMM/jDD')
        : '',
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  const submitMutation = useMutation({
    mutationFn: submitInsuranceProfile,
    onSuccess,
    onError: (error: IErrorResponse) => {
      setErrorMessage(error.formatMessage());
    },
  });

  const onSubmit = (data: ProfileFormData) => {
    setErrorMessage('');
    setShowSlowResponseWarning(false);
    submitMutation.mutate({
      birthDate: moment(data.birthDate, 'jYYYY/jMM/jDD').format('YYYY-MM-DD'),
    });
  };

  useEffect(() => {
    if (!submitMutation.isPending) {
      setShowSlowResponseWarning(false);
      return;
    }

    const timer = setTimeout(() => {
      setShowSlowResponseWarning(true);
    }, SLOW_RESPONSE_THRESHOLD_MS);

    return () => clearTimeout(timer);
  }, [submitMutation.isPending]);

  useEffect(() => {
    if (!open) {
      setErrorMessage('');
      setShowSlowResponseWarning(false);
      reset({
        birthDate: customerInfo?.birthDate
          ? moment(customerInfo.birthDate).format('jYYYY/jMM/jDD')
          : '',
      });
    }
  }, [open, customerInfo?.birthDate, reset]);

  return {
    control,
    handleSubmit,
    onSubmit,
    errors,
    isValid,
    isPending: submitMutation.isPending,
    errorMessage,
    clearErrorMessage: () => setErrorMessage(''),
    showSlowResponseWarning,
  };
};
